import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import styled from "@emotion/styled";
import Title from "../../../components/Title";
import { setColor } from "../../../../../assets/styles/styles";
import Typography from "../../../../../components/common/text/Typography";
import Banner from "../../../../../components/layout/ui/Banner";
import ToggleSwitch from "../../../../../components/common/switchToggle/ToggleSwitch";
import LoadingIcon from "../../../../../components/common/loading/LoadingIcon";
import UploadFile from "../../../../../components/common/fileUploader/FileUploaderInput";
import CreateRoom from "./Step1";
import RoomSettingsPage from "./Step2";
import CreateVanityLink from "./Step3";
import AddVanityLinkData from "./Step4";

const CreateLinkModal = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [successMessageBanner, setSuccessMessageBanner] = useState("");
    const [bannerType, setBannerType] = useState("SUCCESS");
    const [stepNext, setStepNext] = useState("step1");
    const [roomId, setRoomId] = useState("");
    const [vanityId, setVanityId] = useState("");
    const [roomSettingsId, setRoomSettingsId] = useState("");
    const [countdown, setCountdown] = useState(5);
    const [clientName, setClientName] = useState("");

    useEffect(() => {
        if (stepNext === "allStepsCompleted") {
            const interval = setInterval(() => {
                setCountdown((prevCount) => {
                    if (prevCount === 1) {
                        clearInterval(interval);
                        queryClient.invalidateQueries(["vanityData"]);
                        navigate("/console/vanity-links");
                    }
                    return prevCount - 1;
                });
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [stepNext, navigate, queryClient]);

    useEffect(() => {
        if (successMessageBanner) {
            const timer = setTimeout(() => {
                setSuccessMessageBanner("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessageBanner]);

    return (
        <>
            <Banner
                width={"100%"}
                zIndex="1000"
                message={successMessageBanner}
                type={bannerType}
                openBanner={!!successMessageBanner}
                setOpenBanner={setSuccessMessageBanner}
                topDisplay
                showCloseButton
                gameScreen={false}
                top={"0px"}
                left={"0"}
            />
            <Title
                title={"Vanity Links"}
                description={
                    "Manage all links in Intraverse. Changes made here are automatically pushed to related environments."
                }
            />
            <ModalContent>
                <GeneralContainer>
                    <Typography variant="h2PurpleHeading" margin={`0 0 2rem 0`} fontSize={"24px"}>
                        {stepNext === "step1" && "Step 1: Create Room"}
                        {stepNext === "step2" && "Step 2: Update Room Settings"}
                        {stepNext === "step3" && "Step 3: Create Vanity Link"}
                        {stepNext === "step4" && "Step 4: Add Vanity Link Data"}
                        {stepNext === "allStepsCompleted" && "Summary"}
                    </Typography>

                    {stepNext === "step1" && (
                        <>
                            <CreateRoom
                                setSuccessMessageBanner={setSuccessMessageBanner}
                                setBannerType={setBannerType}
                                setStepNext={setStepNext}
                                setRoomId={setRoomId}
                            />
                        </>
                    )}
                    {stepNext === "step2" && (
                        <>
                            <RoomSettingsPage
                                roomId={roomId}
                                setRoomSettingsId={setRoomSettingsId}
                                setStepNext={setStepNext}
                            />
                        </>
                    )}
                    {stepNext === "step3" && (
                        <>
                            <CreateVanityLink
                                roomId={roomId}
                                roomSettingsId={roomSettingsId}
                                setStepNext={setStepNext}
                                setVanityId={setVanityId}
                                setSuccessMessageBanner={setSuccessMessageBanner}
                                setBannerType={setBannerType}
                                setClientName={setClientName}
                            />
                        </>
                    )}
                    {stepNext === "step4" && (
                        <>
                            <AddVanityLinkData
                                roomId={roomId}
                                roomSettingsId={roomSettingsId}
                                setStepNext={setStepNext}
                                vanityId={vanityId}
                                setSuccessMessageBanner={setSuccessMessageBanner}
                                setBannerType={setBannerType}
                                clientName={clientName}
                            />
                        </>
                    )}

                    {stepNext === "allStepsCompleted" && (
                        <>
                            <Card>
                                <Typography variant="ConsoleDescription" margin={`0 0 20px 0`}>
                                    Room ID: {roomId}
                                </Typography>
                                <Typography variant="ConsoleDescription" margin={`0 0 20px 0`}>
                                    Room Settings ID: {roomSettingsId}
                                </Typography>
                                <Typography variant="ConsoleDescription" margin={`0 0 20px 0`}>
                                    Vanity Link ID: {vanityId}
                                </Typography>

                                <Typography variant="ConsoleDescription" margin="0 0 20px 0">
                                    {`Redirecting to Vanity Links Page in ${countdown} second${countdown !== 1 ? "s" : ""}...`}
                                </Typography>
                            </Card>
                        </>
                    )}
                </GeneralContainer>
            </ModalContent>
        </>
    );
};

export default CreateLinkModal;

const LoadingIconContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    color: ${setColor.inputText};
`;

const Card = styled.div`
    width: 100%;
    height: auto;
    position: relative;
    padding: 20px;
    @media (max-width: 768px) {
        width: 100%;
        padding: 15px 0;
    }
`;

const GeneralContainer = styled.div`
    width: 80%;
    font-family: "Barlow-Regular", Helvetica;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: center;
    margin-top: 15px;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;
